// SPDX-License-Identifier: BUSL-1.1

/**
 * Display + summary helpers for the agent-detail surface.
 *
 * Extracted from `routes/agent-detail.tsx` during the P3b refactor.
 * Pure functions — no React, no hooks.
 */

import type { Session } from '@/types/api';
import type { ActivityEvent, ActivityEventKind } from './types';
import { parseActivityPayload } from './activity-payload-schemas';

/** Get duration from session - prefers stored duration, falls back to activeSeconds */
export function getSessionDuration(session: Session): number {
  // Prefer stored durationSeconds (set when session ends)
  if (session.durationSeconds != null && session.durationSeconds > 0) {
    return session.durationSeconds;
  }
  // Fall back to activeSeconds (calculated on the fly)
  if (session.activeSeconds != null && session.activeSeconds > 0) {
    return session.activeSeconds;
  }
  // Calculate from startedAt to now (for active sessions)
  if (session.startedAt) {
    const started = new Date(session.startedAt).getTime();
    const now = Date.now();
    return Math.floor((now - started) / 1000);
  }
  return 0;
}

export function formatDuration(seconds: number): string {
  if (seconds <= 0) return '-';
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}m ${seconds % 60}s`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ${mins % 60}m`;
}

/** Estimate cost using per-token rates or use stored cost */
export function estimateCost(session: Session): string {
  if (session.cost != null) {
    const cost = typeof session.cost === 'string' ? parseFloat(session.cost) : session.cost;
    if (!isNaN(cost) && cost > 0) {
      return `$${cost.toFixed(4)}`;
    }
  }
  const inputCost = (session.inputTokens / 1_000_000) * 3;
  const outputCost = (session.outputTokens / 1_000_000) * 15;
  return `$${(inputCost + outputCost).toFixed(4)}`;
}

export function formatTimeAgo(dateStr: string | null): string {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  return `${diffDays}d ago`;
}

export const EVENT_KIND_COLORS: Record<ActivityEventKind, string> = {
  tool_call: 'bg-blue-900/40 text-blue-300',
  artifact: 'bg-emerald-900/40 text-emerald-300',
  decision: 'bg-violet-900/40 text-violet-300',
  context_compressed: 'bg-cyan-900/40 text-cyan-300',
  blocker: 'bg-red-900/40 text-red-300',
  task_started: 'bg-amber-900/40 text-amber-300',
  task_completed: 'bg-green-900/40 text-green-300',
};

/**
 * Render a one-line summary for each event kind. Server validates the payload
 * shape per kind via Zod (see agent-activity.ts:PAYLOAD_SCHEMAS); the
 * UI-side schemas in `activity-payload-schemas.ts` mirror those exactly so
 * the rendering branches operate on a discriminated union, not on
 * `Record<string, unknown>`. The `unknown` branch fires only on schema drift
 * (UI deploy lag relative to server) — falls back to a JSON dump and logs.
 */
export function renderEventSummary(event: ActivityEvent): string {
  const parsed = parseActivityPayload(event.kind, event.payload ?? {});
  switch (parsed.kind) {
    case 'tool_call': {
      const { tool, ms, args_summary } = parsed.data;
      const msPart = ms !== undefined ? ` · ${ms}ms` : '';
      const argsPart = args_summary ? ` (${args_summary})` : '';
      return `${tool}${argsPart}${msPart}`;
    }
    case 'artifact': {
      const { type, title, uri } = parsed.data;
      return `${type}: ${title ?? uri}`;
    }
    case 'decision':
      return parsed.data.summary;
    case 'context_compressed': {
      const { before_tokens, after_tokens, ratio } = parsed.data;
      return `${before_tokens} → ${after_tokens} tokens (×${ratio.toFixed(2)})`;
    }
    case 'blocker': {
      const { kind, detail } = parsed.data;
      // Provenance prefix — `stall_suspected` is the only blocker kind the
      // agent doesn't self-emit. It's auto-fired by the server-side HB-04
      // watchdog when an agent stops producing activity events. Marking it
      // visually keeps operators from chasing the wrong remediation path
      // (a stall is "agent went silent", not "agent reported an issue").
      if (kind === 'stall_suspected') {
        return `🤖 stall detected — ${detail}`;
      }
      return `[${kind.replace(/_/g, ' ')}] ${detail}`;
    }
    case 'task_started': {
      const { task_id, title } = parsed.data;
      return title ? `${task_id} — ${title}` : task_id;
    }
    case 'task_completed': {
      const { task_id, outcome } = parsed.data;
      return outcome ? `${task_id} (${outcome})` : task_id;
    }
    case 'unknown':
      // Schema drift — server has a payload shape the UI doesn't recognize.
      // Logged in parseActivityPayload(); show truncated JSON so operators
      // can still diagnose.
      return JSON.stringify(parsed.raw).slice(0, 80);
  }
}
